import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "antd-mobile";
import {
  getOrderList,
  type OrderListParams,
  type OrderListItem,
  type MainOrderStatus,
  type OrderProductType,
  REFUND_STATUS_MAP,
  resolveExpertOrderStep,
  resolveExpertOrderStatusText,
} from "@/api/order";

import {
  getGroupTourOrderList,
  cancelGroupTourOrder,
  cancelGroupTourRefund,
} from "../route-detail-product/api/order";
import {
  cancelTourOrder,
  type TourOrderListItem,
  type TourOrderListParams,
} from "@/api/tourOrder";
import { cancelHotelOrder } from "@/api/hotel";
import { cancelCarOrder } from "@/api/car";
import {
  cancelTourRefund,
  cancelHotelRefund,
  cancelCarRefund,
} from "@/api/afterSale";
import { useLoadMore } from "@/hooks/useLoadMore";

import SingleOrderCard from "./components/SingleOrderCard";
import { AfterSaleModal, ReviewModal, InvoiceModal } from "@/components/modals";

const tabs = [
  { label: "全部", value: undefined },
  { label: "待支付", value: 0 },
  { label: "待确认", value: 1 },
  { label: "待签约", value: 2 },
  { label: "待出行", value: 3 },
  { label: "已完成", value: 7 },
  { label: "售后", value: -1 },
];

/** 产品类型图标 */
const typeIconMap: Record<string, string> = {
  car: "\uD83D\uDE97",
  hotel: "\uD83C\uDFE8",
  tour: "\uD83D\uDDFA\uFE0F",
  guide: "\uD83D\uDC69\u200D\u2708\uFE0F",
};

/**
 * 数字状态码 + 类型 → 主状态文字映射
 *
 * hotel: 0=待支付 1=已支付(待确认) 2=已确认(待入住) 4=已入住 5=已取消 6=已退款 7=已完成
 * tour:  0=待支付 1=已支付(待商家确认) 2=已接单(待上传合同) 3=待出行 4=已出行(使用中) 5=已取消 6=已退款 7=已完成
 * car:   与 tour 状态码一致
 * guide: 同 tour
 */
function resolveMainStatus(status: number, type: string): MainOrderStatus {
  const isTourType = type === "tour" || type === "guide";
  const isCar = type === "car";
  const tourLike = isTourType || isCar;

  switch (status) {
    case 0:
      return "待支付";
    case 1:
      return tourLike ? "待商家确认" : "待确认";
    case 2:
      return tourLike ? "待签约" : "待入住";
    case 3:
      return "待出行"; // tour/car 主流程；酒店若出现该码亦按此展示
    case 4:
      return tourLike ? "使用中" : "已入住";
    case 5:
      return "已取消";
    case 6:
      return "售后中"; // 已退款归类到售后
    case 7:
      return "已完成";
    default:
      return "待出行";
  }
}

type SingleOrderProductType = "hotel" | "car" | "tour" | "guide" | undefined;

/**
 * 将团游订单列表项映射为统一 OrderListItem 结构
 */
function mapGroupTourToOrderListItem(item: TourOrderListItem): OrderListItem {
  const raw = item as Record<string, unknown>;
  const status = item.status as number;

  // 新团游接口：status=6 表示已退款，需推导售后字段适配旧 UI
  const isRefundStatus = status === 6;
  const hasRefund = (raw.has_refund as number) ?? (isRefundStatus ? 1 : 0);
  const refundStatus = (raw.refund_status as number) ?? (isRefundStatus ? 20 : 0);

  return {
    id: item.id,
    order_no: item.order_no,
    type: "tour",
    has_comment: 0,
    has_invoice: 0,
    invoice_status: null,
    type_text: "线路",
    remark: item.trip_description,
    product_title: item.title,
    product_id: 0,
    cover_url: item.cover,
    total_amount: item.total_amount,
    status,
    status_text: item.status_text,
    start_date: item.start_date,
    end_date: item.end_date,
    total_people: item.total_people,
    created_at: item.add_time,
    pay_time: undefined,
    has_refund: hasRefund,
    refund_id: (raw.refund_id as number) ?? undefined,
    refund_status: refundStatus,
    refund_status_text: (raw.refund_status_text as string) ?? undefined,
  };
}

const MAIN_STATUS_STEP_MAP = {
  tourLike: { 0: 0, 1: 1, 2: 2, 3: 3 } as Record<number, number>,
  hotelLike: { 0: 0, 1: 1, 2: 2 } as Record<number, number>,
} as const;

/**
 * 列表主进度条 step（与 ProgressBarByStep 一致：3 步时 step=3 表示全部完成）
 *
 * 租车 car（仅 3 步：用户下单 / 进行中 / 已完成，见 getMainStatusSteps("car")）：
 * - 售后 hasRefund：-1（与线路一致，不展示步进）
 * - 0 待支付：「用户下单」未完成 → step=0（当前落在第一步）
 * - 1～6 已支付及后续流程：「用户下单」已完成，当前为「进行中」→ step=1
 * - 7 已完成：三步全部完成 → step=3
 */
function resolveMainStatusStep(
  status: number,
  productType: SingleOrderProductType,
  hasRefund: boolean,
  refund_status: number,
): number {
  if ((hasRefund && refund_status !== 30) || status === 6 || status === 5) {
    return -1;
  }

  const normalizedStatus = Number.isFinite(status) ? Math.trunc(status) : 0;

  if (productType === "hotel") {
    if (normalizedStatus <= 0) return 0;
    return MAIN_STATUS_STEP_MAP.hotelLike[normalizedStatus] ?? 3;
  }

  // tour / car：5 步进度条
  if (productType === "tour" || productType === "car") {
    if (normalizedStatus <= 0) return 0;
    if (normalizedStatus === 7) return 5;
    return MAIN_STATUS_STEP_MAP.tourLike[normalizedStatus] ?? 4;
  }
  return 0;
}

export default function OrdersPage() {
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const [afterSaleOrder, setAfterSaleOrder] = useState<OrderListItem | null>(
    null,
  );
  const [reviewOrder, setReviewOrder] = useState<OrderListItem | null>(null);
  const [invoiceOrder, setInvoiceOrder] = useState<OrderListItem | null>(null);

  /** 打开售后弹窗 */
  const handleAfterSale = (order: OrderListItem) => {
    setAfterSaleOrder(order);
  };

  /** 打开评价弹窗 */
  const handleReview = (order: OrderListItem) => {
    setReviewOrder(order);
  };

  /** 打开申请开票弹窗 */
  const handleInvoice = (order: OrderListItem) => {
    setInvoiceOrder(order);
  };

  /** 取消售后：弹出确认框 */
  const handleCancelRefund = (order: OrderListItem) => {
    Dialog.show({
      content: "确认要取消该售后申请吗？",
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "再想想",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "确认取消",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: async () => {
              if (!order.refund_id) return;
              try {
                if (order.type === "tour") {
                  await cancelGroupTourRefund({ refund_id: order.refund_id });
                } else if (order.type === "guide") {
                  await cancelTourRefund(order.refund_id);
                } else if (order.type === "hotel") {
                  await cancelHotelRefund(order.refund_id);
                } else if (order.type === "car") {
                  await cancelCarRefund(order.refund_id);
                }
                window.location.reload();
              } catch (err: any) {
                console.error("取消售后失败:", err?.message || err);
              }
            },
          },
        ],
      ],
    });
  };

  /** 取消订单：弹出 antd-mobile Dialog 确认框 */
  const handleCancel = (order: OrderListItem) => {
    Dialog.show({
      content: `确认取消订单 ${order.order_no}？取消后无法恢复。`,
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "再想想",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "确认取消",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: async () => {
              try {
                if (order.type === "tour") {
                  await cancelGroupTourOrder({ id: order.id, reason: "用户主动取消" });
                } else if (order.type === "guide") {
                  await cancelTourOrder({ id: order.id, reason: "用户主动取消" });
                } else if (order.type === "hotel") {
                  await cancelHotelOrder({ id: order.id, reason: "用户主动取消" });
                } else if (order.type === "car") {
                  await cancelCarOrder(order.id, "用户主动取消");
                } else {
                  return;
                }
                window.location.reload();
              } catch (err: any) {
                console.error("取消失败:", err?.message || err);
              }
            },
          },
        ],
      ],
    });
  };

  // 上拉加载（团游 + 租车/酒店/达人 合并）
  const { list, loading, hasMore, containerRef, reload } = useLoadMore<OrderListItem>({
    pageSize: 10,
    deps: [activeTab],
    fetcher: async (page) => {
      const status = tabs[activeTab].value;

      // 团游订单
      const groupParams: TourOrderListParams = { page, limit: 10 };
      if (status !== undefined) groupParams.status = status as TourOrderListParams["status"];

      // 其他订单（租车/酒店/达人）
      const orderParams: OrderListParams = { page, limit: 10 };
      if (status !== undefined) orderParams.status = status;

      const [groupRes, orderRes] = await Promise.all([
        getGroupTourOrderList(groupParams).catch(() => null),
        getOrderList(orderParams).catch(() => null),
      ]);

      const groupList = groupRes?.data?.code === 200
        ? groupRes.data.data.list.map(mapGroupTourToOrderListItem)
        : [];
      const orderList = orderRes?.data?.code === 200
        ? orderRes.data.data.list
        : [];

      // 合并并按创建时间倒序
      const merged = [...groupList, ...orderList].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      const groupTotal = groupRes?.data?.code === 200 ? groupRes.data.data.total : 0;
      const orderTotal = orderRes?.data?.code === 200 ? orderRes.data.data.pagination.total : 0;

      return { list: merged, total: groupTotal + orderTotal, page, limit: 10 };
    },
  });

  // 将 API 数据适配为组件所需的 Order 类型（对应 GET /home/order/all）
  const adaptToSingleOrder = (item: OrderListItem) => {
    // type 字段映射
    const pType = (item.type ?? undefined) as
      | ("hotel" | "car" | "tour" | "guide")
      | undefined;

    const isGuideType = pType === "guide";

    // 售后状态覆盖
    const hasRefund = item.has_refund === 1;
    let mainStatus: MainOrderStatus | string;

    if (isGuideType) {
      // 达人订单：使用 BLW status 数值映射
      mainStatus = resolveExpertOrderStatusText(item.status);
      if (hasRefund && item.refund_status !== 30) {
        mainStatus =
          REFUND_STATUS_MAP[item.refund_status] ?? mainStatus;
      }
    } else {
      // C端订单：原有映射逻辑
      mainStatus = resolveMainStatus(item.status, item.type);
      if (hasRefund && item.refund_status !== 30) {
        mainStatus = REFUND_STATUS_MAP[item.refund_status] || mainStatus;
      }
    }

    // 达人订单使用专用6步进度映射（status + is_depart）
    const expertStep = isGuideType
      ? resolveExpertOrderStep(item.status, (item as any).is_depart ?? 0)
      : null;

    return {
      id: String(item.id),
      orderNo: item.order_no,
      type: "single" as const,
      has_comment: item.has_comment,
      has_invoice: item.has_invoice,
      mainStatus: mainStatus as MainOrderStatus,
      invoice_status: item.invoice_status,
      cover_url: item.cover_url,
      mainStatusStep:
        expertStep ?? resolveMainStatusStep(
          item.status,
          pType,
          hasRefund,
          item.refund_status,
        ),
      total: Number(item.total_amount),
      time: item.pay_time || item.created_at,
      destination: "",
      travelers: item.total_people || 0,
      productType: pType,
      productIcon: typeIconMap[item.type] || typeIconMap["tour"],
      productName: item.product_title,
      productSpec: item.remark || "",
      productDate: `${item.start_date} ~ ${item.end_date || ""}${
        item.days
          ? `（${item.days}天${item.nights ? `${item.nights}晚` : ""}）`
          : ""
      }`,
      productSubStatus: hasRefund
        ? REFUND_STATUS_MAP[item.refund_status ?? 0] || "售后中"
        : item.status_text,
      productSubStatusColor: hasRefund ? "#DC2626" : "#6B7280",
      productSubStatusBg: hasRefund ? "#FEE2E2" : "#F3F4F6",
      remark: item.remark,
      hasRefund: item.has_refund,
      refundStatus: item.refund_status,
      isExpert: isGuideType,
    };
  };

  return (
    <div
      className="flex flex-col min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+80px)]"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      {/* 顶部导航 - 固定不动 */}
      <div
        className="flex-shrink-0 bg-white"
        style={{ borderBottom: "1px solid #F0F0F0" }}
      >
        <div
          className="px-5 pb-0"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => navigate(-1)}
                className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
                style={{ background: "#F5F5F5" }}
              >
                <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#374151" }} />
              </button>
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #FF6B35, #FF9A5C)",
                }}
              >
                <i
                  className="ri-plane-line text-white"
                  style={{ fontSize: 15 }}
                />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900 leading-tight">
                  订单中心
                </h1>
                <p className="text-xs text-gray-400 leading-tight">美伊旅行</p>
              </div>
            </div>
            <button
              className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer"
              style={{ background: "#F5F5F5" }}
              onClick={() => navigate("/ai-voice")}
            >
              <i
                className="ri-customer-service-2-line"
                style={{ color: "#6B7280", fontSize: 18 }}
              />
            </button>
          </div>
          <div
            className="flex overflow-x-auto px-1 scrollbar-hide"
            style={{ gap: 24 }}
          >
            {tabs.map((tab, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className="pb-3 text-sm cursor-pointer relative whitespace-nowrap transition-colors flex-shrink-0"
                style={{
                  color: activeTab === i ? "#FF6B35" : "#ACACAC",
                  fontWeight: activeTab === i ? 700 : 400,
                }}
              >
                {tab.label}
                {activeTab === i && (
                  <div
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full"
                    style={{ height: 2.5, width: 18, background: "#FF6B35" }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 订单列表 - 可滚动区域 */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
      >
        {loading && list.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-300">
            <i
              className="ri-loader-4-line animate-spin"
              style={{ fontSize: 36 }}
            />
            <div className="text-sm mt-3 text-gray-400">加载中...</div>
          </div>
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-300">
            <i className="ri-inbox-2-line" style={{ fontSize: 52 }} />
            <div className="text-sm mt-3 text-gray-400">暂无订单，去下单→</div>
          </div>
        ) : (
          <>
            {list.map((order, index) => (
              <SingleOrderCard
                key={`${order.id}-${order.order_no || `idx-${index}`}`}
                order={adaptToSingleOrder(order)}
                onDetail={() => {
                  // 导游/达人订单走 expert 详情页（用 query 确保每次切换刷新）
                  if (order.type === "guide") {
                    navigate(`/order/detail/expert?id=${order.id}`);
                    return;
                  }
                  // 其他类型保持 state 传参
                  navigate(`/order/detail/${order.type}`, {
                    state: { orderId: order.id },
                  });
                }}
                // onSign={() => // 去签约
                //   navigate(`/order/detail/${order.type}`, {
                //     state: {
                //       orderId: order.id,
                //       openContract: true,
                //     },
                //   })
                // }
                onPay={() => {
                  navigate("/payment", {
                    state: {
                      orderNo: order.order_no,
                      totalAmount: String(order.total_amount),
                      type: order.type,
                    },
                  });
                }}
                onCancel={() => handleCancel(order)}
                onReview={() => handleReview(order)}
                onInvoice={() => handleInvoice(order)}
                onReorder={() => {
                  // 达人订单 → 直接进入达人订单详情页
                  if (order.type === "guide") {
                    navigate(`/order/detail/expert?id=${order.id}`);
                    return;
                  }
                  // product_id 无效 → 提示并中止
                  if (!order.product_id) {
                    console.warn("[orders] product_id 为空，无法再次预订:", order);
                    return;
                  }
                  const reorderMap: Record<string, string> = {
                    hotel: `/hotel-detail-product?hotelId=${order.product_id}`,
                    tour: `/route-detail-product?routeId=${order.product_id}`,
                    car: `/car-detail-product?id=${order.product_id}`,
                  };
                  navigate(reorderMap[order.type]);
                }}
                onAfterSale={() => handleAfterSale(order)}
                onCancelRefund={() => handleCancelRefund(order)}
              />
            ))}
            {/* 加载更多 / 到底提示 */}
            <div className="py-4 text-center text-xs text-gray-400">
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-1" />
                  加载中...
                </>
              ) : !hasMore && list.length > 0 ? (
                "- 没有更多了 -"
              ) : null}
            </div>
          </>
        )}
      </div>

      <AfterSaleModal
        visible={!!afterSaleOrder}
        productName={afterSaleOrder?.product_title}
        orderId={afterSaleOrder?.id ? Number(afterSaleOrder.id) : undefined}
        orderType={afterSaleOrder?.type}
        orderAmount={
          afterSaleOrder ? Number(afterSaleOrder.total_amount) : undefined
        }
        onClose={() => setAfterSaleOrder(null)}
        onSubmitSuccess={() => {
          setAfterSaleOrder(null);
          reload(); // 刷新列表 → 售后/开票按钮互斥生效
        }}
      />

      <ReviewModal
        visible={!!reviewOrder}
        orderId={reviewOrder?.id ? Number(reviewOrder.id) : undefined}
        productId={reviewOrder?.product_id}
        productName={reviewOrder?.product_title}
        productType={reviewOrder?.type}
        onClose={() => setReviewOrder(null)}
        onSubmitSuccess={() => {
          setReviewOrder(null);
          reload(); // 刷新列表，评价成功后 has_comment=1 按钮消失
        }}
      />

      {invoiceOrder && (
        <InvoiceModal
          orderId={Number(invoiceOrder.id)}
          productType={(invoiceOrder.type ?? "hotel") as OrderProductType}
          productTitle={invoiceOrder.product_title}
          productSubtitle={[
            invoiceOrder.remark,
            invoiceOrder.days != null
              ? `${invoiceOrder.days}天${
                  invoiceOrder.nights != null ? `${invoiceOrder.nights}晚` : ""
                }`
              : "",
            [invoiceOrder.start_date, invoiceOrder.end_date]
              .filter(Boolean)
              .join(" ~ "),
          ]
            .filter((s) => s && String(s).trim())
            .join(" · ")}
          invoiceAmount={Number(invoiceOrder.total_amount)}
          onClose={() => setInvoiceOrder(null)}
          onSubmitSuccess={() => {
            setInvoiceOrder(null);
            reload(); // 刷新列表 → 售后/开票按钮互斥生效
          }}
        />
      )}
    </div>
  );
}
